import { translateBackend } from '@assets/i18n/config';
import MugImage from '@assets/images/custom/mug.svg';
import { ChevronLeft, Close as CloseIcon } from '@mui/icons-material';
import { colors } from '@ovComponents/0-tokens/ref/palette/palette';
import { Box, RefBox } from '@ovComponents/1-primative/box/box';
import { Link } from '@ovComponents/1-primative/link/link';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Button } from '@ovComponents/2-component/button/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import { IconButton } from '@ovComponents/2-component/iconButton/iconButton';
import { FullScreenDialog } from '@ovComponents/3-pattern/fullScreenDialog/fullScreenDialog';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { UserContext } from '@providers/userContextProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { kebabCase } from 'lodash-es';
import {
  type Dispatch,
  type SetStateAction,
  useContext,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { StepIndicator } from './components/stepIndicator';
import { Account } from './subSteps/account/account';
import { AddModelPortfolioDetails } from './subSteps/addModelPortfolioDetails/addModelPortfolioDetails';
import { AddModelPortfolioSecurities } from './subSteps/addModelPortfolioSecurities/addModelPortfolioSecurities';
import { AddModelPortfoliosIPSAndAssetClasses } from './subSteps/addModelPortfoliosIPSAndAssetClasses/addModelPortfoliosIPSAndAssetClasses';
import { Approval } from './subSteps/approval/approval';
import { ArchiveObjects } from './subSteps/archiveObjects/archiveObjects';
import { AssignPortfolioToSubAccount } from './subSteps/assignPortfolioToSubAccount/assignPortfolioToSubAccount';
import { CollectSigners } from './subSteps/collectSigners/collectSigners';
import { CreateAccount } from './subSteps/createAccount/createAccount';
import { CreateAccountForGoal } from './subSteps/createAccountForGoal/createAccountForGoal';
import { CreateAccountGroup } from './subSteps/createAccountGroup/createAccountGroup';
import { CreateAssetTemplate } from './subSteps/createAssetTemplate/createAssetTemplate';
import { CreateGoal } from './subSteps/createGoal/createGoal';
import { CreateIntegrationFormPack } from './subSteps/createIntegrationFormPack/createIntegrationFormPack';
import { CreateModelPortfolio } from './subSteps/createModelPortfolio/createModelPortfolio';
import { CreateSubAccountForAccount } from './subSteps/createSubAccountForAccount/createSubAccountForAccount';
import { DepositForm } from './subSteps/depositForm/depositForm';
import { DepositReview } from './subSteps/depositReview/depositReview';
import { DepositType } from './subSteps/depositType/depositType';
import { Documents } from './subSteps/documents/documents';
import { EditAccount } from './subSteps/editAccount/editAccount';
import { EditGoal } from './subSteps/editGoal/editGoal';
import { EmploymentInformation } from './subSteps/employmentInformation/employmentInformation';
import { FinancialProjection } from './subSteps/financialProjection/financialProjection';
import { GoalRiskQuestion } from './subSteps/goalRiskQuestion/goalRiskQuestion';
import { Goals } from './subSteps/goals/goals';
import { Household } from './subSteps/household/household';
import { IdVerification } from './subSteps/idVerification/idVerification';
import { IncomeAssetsAndDebts } from './subSteps/incomeAssetsAndDebts/incomeAssetsAndDebts';
import { InvestmentKnowledge } from './subSteps/investmentKnowledge/investmentKnowledge';
import { ManageFormCompletions } from './subSteps/manageFormCompletions/manageFormCompletions';
import { ManageSigners } from './subSteps/manageSigners/manageSigners';
import { NetWorthAdd } from './subSteps/netWorth/netWorthAdd';
import { NonIndividualInformation } from './subSteps/nonIndividualInformation/nonIndividualInformation';
import { NonIndividualSetup } from './subSteps/nonIndividualSetup/nonIndividualSetup';
import { PersonalInformation } from './subSteps/personalInformation/personalInformation';
import { PersonsOfInterest } from './subSteps/personsOfInterest/personsOfInterest';
import { PrepareFormCompletions } from './subSteps/prepareFormCompletions/prepareFormCompletions';
import { RedirectHandler } from './subSteps/redirectHandler/redirectHandler';
import { RelatedEntities } from './subSteps/relatedEntities/relatedEntities';
import { RelationshipInformation } from './subSteps/relationshipInformation/relationshipInformation';
import { ResidencyInformation } from './subSteps/residencyInformation/residencyInformation';
import { Review } from './subSteps/review/review';
import { ReviewAccount } from './subSteps/reviewAccount/reviewAccount';
import { ReviewBankAccountInformation } from './subSteps/reviewBankAccountInformation/reviewBankAccountInformation';
import { ReviewGoal } from './subSteps/reviewGoal/reviewGoal';
import { ReviewPortfolioChanges } from './subSteps/reviewPortfolioChanges/reviewPortfolioChanges';
import { RiskProfile } from './subSteps/riskProfile/riskProfile';
import { RiskQuestion } from './subSteps/riskQuestion/riskQuestion';
import { SendIntegrationFormPack } from './subSteps/sendIntegrationFormPack/sendIntegrationFormPack';
import { SignFormCompletions } from './subSteps/signFormCompletions/signFormCompletions';
import { SimpleForm } from './subSteps/simpleForm/simpleForm';
import { SubAccounts } from './subSteps/subAcounts/subAccounts';
import { SynchronousIntegration } from './subSteps/synchronousIntegration/synchronousIntegration';
import { Transfer } from './subSteps/transfer/transfer';
import { TypeSelect } from './subSteps/typeSelect/typeSelect';
import { UserVerification } from './subSteps/userVerification/userVerification';
import { WithdrawForm } from './subSteps/withdrawForm/withdrawForm';
import { WithdrawReview } from './subSteps/withdrawReview/withdrawReview';
import WorkflowReview from './subSteps/workflowReview/workflowReview';
import { WorkflowCompletionContext } from './workflowCompletionDialogContext';

export const WorkflowCompletionVisual = ({
  workflowCompletion,
  open,
  onClose,
  onNext,
  previousStep,
  loading,
  data,
  onChangeSetData,
  setActiveWorkflowCompletion,
  isInnerWorkflow,
  handleIdSkip,
  onCloseIconClick = onClose,
  filterReviewStepId,
}: {
  workflowCompletion: any;
  open: boolean;
  onClose: () => void;
  onCloseIconClick?: () => any;
  onNext: (branchId?: string) => void;
  previousStep: () => void;
  loading: boolean;
  data?: any;
  onChangeSetData?: Dispatch<SetStateAction<any>>;
  setActiveWorkflowCompletion?: any;
  isInnerWorkflow?: boolean;
  handleIdSkip?: () => void;
  filterReviewStepId?: string;
}) => {
  const { userContext, activeOrganization } = useContext(UserContext);
  const [hasQuebecAddress, setHasQuebecAddress] = useState(false);
  const [openSkipIDVerification, setOpenSkipIDVerification] = useState(false);
  const ref = useRef<HTMLInputElement | null>(null);
  const { sendAnalytic } = useContext(AnalyticsContext);

  const { t } = useTranslation('workflowCompletions');
  const { sys } = useThemeTokens();
  const handleSkip = async () => {
    if (
      hasQuebecAddress &&
      workflowCompletion?.currentSubStep?.skippable !== true
    ) {
      setOpenSkipIDVerification(true);
    } else {
      await onNext();
    }
  };
  if (
    filterReviewStepId &&
    workflowCompletion?.currentSubStep?.id === filterReviewStepId
  ) {
    onClose();
  }
  const handleGoBack = () => {
    if (hasQuebecAddress) setHasQuebecAddress(false);
    sendAnalytic(ovAnalyticsEvents.modelPageCreateBack, {
      stepName: 'CREATE_MODEL_PORTFOLIO',
      workflowCompletionId: workflowCompletion?.id,
      timestamp: new Date().toISOString(),
    });
    previousStep();
  };
  const noMaxWidth = (stepType: string) => ['WITHDRAW_FORM'].includes(stepType);
  const subStepMapping = (subStep: any) => {
    switch (subStep?.type) {
      case 'ACCOUNTS':
        return (
          <Account
            options={subStep.options}
            onNext={onNext}
            stepLoading={loading}
            userId={workflowCompletion.objectId}
          />
        );
      case 'APPROVAL':
        return (
          <Approval
            objectId={workflowCompletion.objectId}
            objectType={workflowCompletion.objectType}
            options={subStep.options}
            onNext={onNext}
            stepLoading={loading}
          />
        );
      case 'DOCUMENTS':
        return (
          <Documents
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            objectId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
            objectType={workflowCompletion.objectType}
          />
        );
      case 'EMPLOYMENT_INFORMATION':
        return (
          <EmploymentInformation
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );
      case 'GOALS':
        return (
          <Goals
            options={subStep.options}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );
      case 'HOUSEHOLD':
        return (
          <Household
            options={subStep.options}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );
      case 'ID_VERIFICATION':
        return (
          <IdVerification
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );
      case 'INCOME_ASSETS_AND_DEBTS':
        return (
          <IncomeAssetsAndDebts
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );
      case 'INVESTMENT_KNOWLEDGE':
        return (
          <InvestmentKnowledge
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );
      case 'CREATE_ASSET_TEMPLATE':
        return (
          <CreateAssetTemplate
            options={subStep.options}
            onNext={onNext}
            stepLoading={loading}
          />
        );
      case 'CREATE_MODEL_PORTFOLIO':
        return (
          <CreateModelPortfolio
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            onNext={onNext}
            stepLoading={loading}
            onBack={previousStep}
          />
        );
      case 'ADD_MODEL_PORTFOLIO_DETAILS':
        return (
          <AddModelPortfolioDetails
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            onNext={onNext}
            stepLoading={loading}
          />
        );
      case 'ADD_MODEL_PORTFOLIO_SECURITIES':
        return (
          <AddModelPortfolioSecurities
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );
      case 'ADD_IPS_AND_ASSET_CLASSES_DISTRIBUTION':
        return (
          <AddModelPortfoliosIPSAndAssetClasses
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            onNext={onNext}
            stepLoading={loading}
          />
        );
      case 'PERSONAL_INFORMATION':
        return (
          <PersonalInformation
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );
      case 'USER_VERIFICATION':
        return (
          <UserVerification
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );
      case 'PERSONS_OF_INTEREST':
        return (
          <PersonsOfInterest
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );
      case 'RELATED_ENTITIES':
        return (
          <RelatedEntities
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );
      case 'RELATIONSHIP_INFORMATION':
        return (
          <RelationshipInformation
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );
      case 'RESIDENCY_INFORMATION':
        return (
          <ResidencyInformation
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );
      case 'RISK_QUESTION_1':
        return (
          <RiskQuestion
            question={1}
            workflowCompletion={workflowCompletion}
            options={subStep.options}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );
      case 'RISK_QUESTION_2':
        return (
          <RiskQuestion
            question={2}
            workflowCompletion={workflowCompletion}
            options={subStep.options}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );
      case 'SUB_ACCOUNTS':
        return (
          <SubAccounts
            options={subStep.options}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );
      case 'CREATE_GOAL':
        return (
          <CreateGoal
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            objectId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
            objectType={workflowCompletion.objectType}
          />
        );
      case 'EDIT_GOAL':
        return (
          <EditGoal
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            objectId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
            objectType={workflowCompletion.objectType}
          />
        );
      case 'REVIEW_GOAL':
        return (
          <ReviewGoal
            options={subStep.options}
            onNext={onNext}
            userId={workflowCompletion.objectId}
            setActiveWorkflowCompletion={setActiveWorkflowCompletion}
            workflowCompletion={workflowCompletion}
          />
        );
      case 'WORKFLOW_REVIEW':
        return (
          <WorkflowReview
            options={subStep.options}
            onNext={onNext}
            workflowCompletion={workflowCompletion}
            userId={workflowCompletion.objectId}
          />
        );
      case 'GOAL_RISK_QUESTION_1':
        return (
          <GoalRiskQuestion
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            objectId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
            objectType={workflowCompletion.objectType}
          />
        );
      case 'CREATE_ACCOUNT_FOR_GOAL':
        return (
          <CreateAccountForGoal
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            objectId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
            objectType={workflowCompletion.objectType}
          />
        );
      case 'EDIT_ACCOUNT':
        return (
          <EditAccount
            direction={workflowCompletion?.direction ?? 'FORWARD'}
            options={subStep.options}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
            previousStep={previousStep}
            workflowCompletion={workflowCompletion}
          />
        );
      case 'REVIEW_ACCOUNT':
        return (
          <ReviewAccount
            options={subStep.options}
            onNext={onNext}
            setActiveWorkflowCompletion={setActiveWorkflowCompletion}
            workflowCompletion={workflowCompletion}
            userId={workflowCompletion.objectId}
          />
        );
      case 'CREATE_SUBACCOUNT_FOR_ACCOUNT':
        return (
          <CreateSubAccountForAccount
            options={subStep.options}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );
      case 'ASSIGN_PORTFOLIO_TO_SUB_ACCOUNT':
        return (
          <AssignPortfolioToSubAccount
            options={subStep.options}
            objectId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
            workflowCompletion={workflowCompletion}
            objectType={workflowCompletion.objectType}
          />
        );
      case 'REVIEW_PORTFOLIO_CHANGES':
        return (
          <ReviewPortfolioChanges
            options={subStep.options}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );
      case 'TRANSFERS':
        return (
          <Transfer
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            onNext={onNext}
            stepLoading={loading}
            userId={workflowCompletion.objectId}
          />
        );
      case 'DEPOSIT_TYPE':
        return (
          <DepositType
            workflowCompletion={workflowCompletion}
            options={subStep.options}
            onNext={onNext}
            direction={workflowCompletion?.direction ?? 'FORWARD'}
            stepLoading={loading}
          />
        );
      case 'DEPOSIT_FORM':
        return (
          <DepositForm
            workflowCompletion={workflowCompletion}
            options={subStep.options}
            onNext={onNext}
            stepLoading={loading}
          />
        );
      case 'DEPOSIT_REVIEW':
        return (
          <DepositReview
            options={subStep.options}
            onNext={onNext}
            workflowCompletion={workflowCompletion}
            setActiveWorkflowCompletion={setActiveWorkflowCompletion}
          />
        );
      case 'WITHDRAW_FORM':
        return <WithdrawForm options={subStep.options} onNext={onNext} />;
      case 'WITHDRAW_REVIEW':
        return <WithdrawReview options={subStep.options} onNext={onNext} />;
      case 'SIMPLE_FORM':
        return (
          data &&
          onChangeSetData && (
            <SimpleForm
              options={subStep.options}
              onNext={onNext}
              onChangeSetData={onChangeSetData}
              data={data}
              stepLoading={loading}
            />
          )
        );
      case 'TYPE_SELECT':
        return (
          data &&
          onChangeSetData && (
            <TypeSelect
              options={subStep.options}
              onNext={onNext}
              onChangeSetData={onChangeSetData}
              data={data}
            />
          )
        );
      case 'NON_INDIVIDUAL_INFORMATION':
        return (
          <NonIndividualInformation
            workflowCompletion={workflowCompletion}
            options={subStep.options}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );
      case 'NON_INDIVIDUAL_SETUP':
        return (
          <NonIndividualSetup
            workflowCompletion={workflowCompletion}
            options={subStep.options}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
            workflowCompletionId={workflowCompletion.id}
          />
        );
      case 'PROJECTIONS':
        return (
          <FinancialProjection
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            onNext={onNext}
            stepLoading={loading}
          />
        );
      case 'FINANCIAL_PROJECTIONS':
        return (
          <FinancialProjection
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            onNext={onNext}
            stepLoading={loading}
          />
        );
      case 'REVIEW':
        return (
          <Review
            options={subStep.options}
            onNext={onNext}
            stepLoading={loading}
          />
        );
      case 'REVIEW_BANK_ACCOUNT_INFORMATION':
        return (
          <ReviewBankAccountInformation
            options={subStep.options}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );
      case 'RISK_PROFILE':
        return (
          <RiskProfile
            options={subStep.options}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );
      case 'COLLECT_SIGNERS':
        return (
          <CollectSigners
            options={subStep.options}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );
      case 'CREATE_ACCOUNT':
        return (
          <CreateAccount
            options={subStep.options}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
            workflowCompletion={workflowCompletion.id}
          />
        );
      case 'PREPARE_FORM_COMPLETIONS':
        return (
          <PrepareFormCompletions
            workflowCompletion={workflowCompletion}
            onNext={onNext}
            onBack={previousStep}
            direction={workflowCompletion?.direction ?? 'FORWARD'}
          />
        );
      case 'MANAGE_SIGNERS':
        return (
          <ManageSigners
            workflowCompletion={workflowCompletion}
            onNext={onNext}
            options={subStep.options}
          />
        );
      case 'CREATE_INTEGRATION_FORM_PACK':
        return (
          <CreateIntegrationFormPack
            workflowCompletion={workflowCompletion}
            onNext={onNext}
            onBack={previousStep}
            direction={workflowCompletion?.direction ?? 'FORWARD'}
          />
        );
      case 'SEND_INTEGRATION_FORM_PACK':
        return (
          <SendIntegrationFormPack
            workflowCompletion={workflowCompletion}
            onNext={onNext}
            onBack={previousStep}
            direction={workflowCompletion?.direction ?? 'FORWARD'}
          />
        );
      case 'SIGN_FORM_COMPLETIONS':
        return (
          <SignFormCompletions
            workflowCompletion={workflowCompletion}
            onNext={onNext}
            options={subStep.options}
          />
        );
      case 'MANAGE_FORM_COMPLETIONS':
        return (
          <ManageFormCompletions
            workflowCompletion={workflowCompletion}
            onNext={onNext}
            options={subStep.options}
            setActiveWorkflowCompletion={setActiveWorkflowCompletion}
          />
        );
      case 'SYNCHRONOUS_INTEGRATION':
        return (
          <SynchronousIntegration
            workflowCompletion={workflowCompletion}
            subStepId={subStep.id}
            onNext={onNext}
            onBack={previousStep}
            options={subStep.options}
            direction={workflowCompletion?.direction ?? 'FORWARD'}
          />
        );
      case 'CREATE_ACCOUNT_GROUP':
        return (
          <CreateAccountGroup
            options={subStep.options}
            objectId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
            objectType={workflowCompletion.objectType}
          />
        );
      case 'NET_WORTH':
        return (
          <NetWorthAdd
            options={subStep.options}
            onNext={onNext}
            workflowCompletion={workflowCompletion}
            subStepId={subStep.id}
          />
        );
      case 'ARCHIVE_OBJECTS':
        return (
          <ArchiveObjects
            options={subStep.options}
            onNext={onNext}
            stepLoading={loading}
          />
        );
      case 'REDIRECT_HANDLER':
        return (
          <RedirectHandler
            workflowCompletion={workflowCompletion}
            onNext={onNext}
            options={subStep.options}
          />
        );
      default:
        return (
          <>
            <Typography variant='displayLarge' sx={{ mt: 1 }}>
              {translateBackend(
                workflowCompletion?.currentSubStep?.options?.title,
              )}
            </Typography>
            <Typography variant='bodyLarge' sx={{ mb: 3 }}>
              {translateBackend(
                workflowCompletion?.currentSubStep?.options?.subtitle,
              )}
            </Typography>
            <Box display='flex' justifyContent='end'>
              <Button
                label={t('workflowCompletions:continue')}
                disabled={loading}
                sx={{ mt: 3, textAlign: 'center' }}
                onClick={onNext}
              />
            </Box>
          </>
        );
    }
  };

  const subStepPermittedRoles =
    workflowCompletion?.currentSubStep?.rolesCompleteableBy ?? [];
  const userRoleProfileId = userContext?.role?.publicRoleProfile?.id;
  const isSubStepRolePermitted =
    subStepPermittedRoles.length === 0 ||
    subStepPermittedRoles
      .map((role: { id: string }) => role?.id)
      .includes(userRoleProfileId);

  const shouldDisplayCloseButton =
    activeOrganization.availableFeatureFlags?.includes('CANCEL_WORKFLOW')
      ? workflowCompletion?.workflow?.settings?.displayCloseButton === undefined
        ? true
        : workflowCompletion?.workflow?.settings?.displayCloseButton
      : true;

  return (
    <WorkflowCompletionContext.Provider
      value={{
        scrollWorkflowDialog: () => {
          if (ref && ref.current) ref?.current?.scrollTo(0, 0);
        },
        setHasQuebecAddress: (value: boolean) => {
          if (workflowCompletion?.currentSubStep?.skippable !== true) {
            setHasQuebecAddress(value);
          }
        },
      }}
    >
      <FullScreenDialog
        isInnerWorkflow={isInnerWorkflow}
        data-testid='workflow-completion-full-screen-dialogue'
        open={open}
      >
        <Box
          display='flex'
          height='100%'
          sx={{ backgroundColor: colors.white }}
        >
          <RefBox ref={ref} flex={3} overflow='auto' pb={12}>
            <Box
              display='flex'
              justifyContent='space-between'
              flexDirection='column'
              height='100%'
            >
              <Box>
                <Box display='flex' justifyContent='space-between' p={3}>
                  {workflowCompletion?.currentSubStep?.canGoBack ? (
                    <Button
                      variant='text'
                      label={t('shared:back')}
                      leadingIcon={ChevronLeft}
                      onClick={() => handleGoBack()}
                    />
                  ) : (
                    <Box />
                  )}
                  {shouldDisplayCloseButton && (
                    <IconButton
                      onClick={onCloseIconClick}
                      testId='workflow-completion-close'
                    >
                      <CloseIcon />
                    </IconButton>
                  )}
                </Box>
                <Box
                  pt='20px'
                  pl={isInnerWorkflow ? 6 : 2}
                  pr={isInnerWorkflow ? 5 : 2}
                  display='flex'
                  justifyContent='center'
                >
                  {isSubStepRolePermitted ? (
                    <Box
                      maxWidth={
                        noMaxWidth(workflowCompletion?.currentSubStep?.type)
                          ? '1000px'
                          : '552px'
                      }
                      width='100%'
                      pb={8}
                      key={workflowCompletion?.currentSubStep?.id}
                    >
                      <StepIndicator workflowCompletion={workflowCompletion} />
                      {subStepMapping(workflowCompletion?.currentSubStep)}
                    </Box>
                  ) : (
                    <Box
                      display='flex'
                      alignItems='center'
                      flexDirection='column'
                      gap={3}
                    >
                      <img src={MugImage} alt='mug-icon' />
                      <Typography
                        variant='displayLarge'
                        sx={{ mt: 1, textAlign: 'center' }}
                      >
                        {t('caughtUp')}
                      </Typography>
                      <Typography
                        variant='bodyLarge'
                        sx={{ mt: 1, maxWidth: '500px', textAlign: 'center' }}
                      >
                        {t('caughtUpSubtitle')}
                      </Typography>
                      <Button
                        variant='text'
                        size='lg'
                        label={t('workflowCompletions:closeWorkflow')}
                        onClick={onClose}
                      />
                    </Box>
                  )}
                </Box>
              </Box>
              {(workflowCompletion?.currentSubStep?.skippable ||
                hasQuebecAddress) && (
                <Box display='flex' justifyContent='end' p={3}>
                  <Button
                    variant='text'
                    label={t('workflowCompletions:skip')}
                    onClick={handleSkip}
                    dataTestId={`skip-button-${kebabCase(workflowCompletion?.currentSubStep?.type)}`}
                  />
                </Box>
              )}
            </Box>
          </RefBox>
        </Box>
      </FullScreenDialog>
      <Dialog open={openSkipIDVerification}>
        <DialogTitle>
          {t('workflowCompletions:idVerificationSkipDialog.title')}
        </DialogTitle>
        <DialogContent>
          <Typography variant='bodyLarge'>
            {t('workflowCompletions:idVerificationSkipDialog.content1')}
            <Link
              underline='none'
              target='_blank'
              href={
                translateBackend(
                  userContext.organization?.translatedSupportUrl,
                ) || 'https://onevest.zendesk.com/hc/en-us/requests/new'
              }
            >
              {t('workflowCompletions:idVerificationSkipDialog.content2')}
            </Link>
            {t('workflowCompletions:idVerificationSkipDialog.content3')}
          </Typography>
        </DialogContent>
        <DialogFooter>
          <Button
            sx={{
              backgroundColor: `${sys.color.primaryVariant} !important`,
              color: `${sys.color.onPrimaryVariant} !important`,
            }}
            label={t('workflowCompletions:idVerificationSkipDialog.cancel')}
            onClick={() => setOpenSkipIDVerification(false)}
          />
          <Button
            sx={{
              backgroundColor: `${sys.color.primary} !important`,
              color: `${sys.color.onPrimary} !important`,
            }}
            label={t('workflowCompletions:idVerificationSkipDialog.continue')}
            onClick={() => {
              if (handleIdSkip) handleIdSkip();
              setOpenSkipIDVerification(false);
              setHasQuebecAddress(false);
              onNext();
            }}
          />
        </DialogFooter>
      </Dialog>
    </WorkflowCompletionContext.Provider>
  );
};

export default WorkflowCompletionVisual;
